import { useState, useEffect } from 'react';
import { Archive } from 'lucide-react';
import { createPortal } from 'react-dom';

type Props = {
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
  /** Label for the entity, e.g. "exécution(s) de test" */
  entityLabel: string;
  count: number;
  isLoading?: boolean;
};

/**
 * Two-step confirmation modal for soft (logical) delete.
 */
export function ConfirmSoftDeleteModal({
  isOpen,
  onConfirm,
  onClose,
  entityLabel,
  count,
  isLoading = false,
}: Props) {
  const [step, setStep] = useState<1 | 2>(1);

  useEffect(() => {
    if (isOpen) setStep(1);
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleClose = () => {
    if (isLoading) return;
    setStep(1);
    onClose();
  };

  const modal =
    step === 1 ? (
      <div className="fixed inset-0 z-[9999] overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 py-8">
          <div
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-[1px]"
            onClick={handleClose}
            aria-hidden
          />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 border border-amber-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-11 h-11 rounded-full bg-amber-100">
                <Archive className="w-5 h-5 text-amber-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Suppression logique</h3>
            </div>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Vous allez archiver <strong>{count}</strong> {entityLabel}. Les enregistrements seront masqués
              de la liste mais conservés en base de données.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Souhaitez-vous continuer vers la confirmation ?
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                Continuer
              </button>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div className={`fixed inset-0 z-[9999] overflow-y-auto ${isLoading ? 'cursor-wait' : ''}`}>
        <div className="flex items-center justify-center min-h-screen px-4 py-8">
          <div
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-[1px]"
            onClick={isLoading ? undefined : handleClose}
            aria-hidden
          />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 border border-amber-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-11 h-11 rounded-full bg-amber-100">
                <Archive className="w-5 h-5 text-amber-800" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirmer l&apos;archivage</h3>
            </div>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Confirmez-vous la suppression logique de <strong>{count}</strong> {entityLabel} ?
              Elles n&apos;apparaîtront plus dans cette liste.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={isLoading}
                className="px-4 py-2 bg-amber-700 text-white rounded-lg hover:bg-amber-800 disabled:opacity-50"
              >
                {isLoading ? 'Archivage…' : 'Archiver'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  return createPortal(modal, document.body);
}
